import { useTranslation } from "react-i18next";
import { Megaphone, Send, SlidersHorizontal, Users } from "lucide-react";
import { AppShell } from "../components/layout/AppShell";
import { NeuButton, NeuCard } from "../components/neu/Neu";
import { AudiencePicker } from "../features/notify/AudiencePicker";
import { NoticeComposer } from "../features/notify/BilingualFields";
import { SentHistory } from "../features/notify/SentHistory";
import { useComposer } from "../features/notify/useComposer";

const Head = ({ icon: Icon, title, hint }) => (
  <div className="flex items-center gap-3">
    <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-2xl bg-background text-primary shadow-neu-sm">
      <Icon className="h-5 w-5" />
    </span>
    <div className="min-w-0">
      <h2 className="text-lg font-black sm:text-base">{title}</h2>
      {hint ? <p className="truncate text-sm text-muted-foreground sm:text-xs">{hint}</p> : null}
    </div>
  </div>
);

export default function CompanyNotifyPage() {
  const { t } = useTranslation();
  const c = useComposer();
  return (
    <AppShell title={t("notify.title")} subtitle={t("notify.subtitle")} titleClassName="text-3xl">
      <div className="grid gap-6" data-testid="company-notify-page">
        <NeuCard className="grid gap-5 p-4 sm:p-6" data-testid="notify-filters-card">
          <Head icon={SlidersHorizontal} title={t("notify.audience")} hint={t("notify.filtersHint")} />
          <AudiencePicker filters={c.filters} setFilters={c.setFilters} lists={c.lists} />
          <div className="flex flex-wrap items-center gap-3">
            <NeuButton type="button" className="flex items-center gap-2 px-5 py-2.5 text-sm sm:text-xs" data-testid="notify-preview" onClick={c.doPreview}>
              <Users className="h-4 w-4" /> {t("notify.preview")}
            </NeuButton>
            {c.count != null ? <span className="text-base font-black text-primary sm:text-sm" data-testid="notify-count">{t("notify.recipients", { n: c.count })}</span> : null}
          </div>
        </NeuCard>

        <NeuCard className="grid gap-5 p-4 sm:p-6" data-testid="notify-message-card">
          <Head icon={Megaphone} title={t("notify.messageTitle")} />
          <NoticeComposer f={c.f} setField={c.setField} translating={c.translating} />
          <div className="grid gap-2">
            <span className="label-xs text-sm sm:text-xs">{t("notify.channelsLabel")}</span>
            <div className="flex flex-wrap items-center gap-3">
              {["push", "email"].map((ch) => (
                <button
                  key={ch}
                  type="button"
                  data-testid={`notify-ch-${ch}`}
                  onClick={() => c.setChannels({ ...c.channels, [ch]: !c.channels[ch] })}
                  className={`rounded-2xl px-5 py-2.5 text-sm font-bold transition-[box-shadow] sm:text-xs ${
                    c.channels[ch] ? "shadow-neu-in text-primary" : "shadow-neu-sm text-muted-foreground"
                  }`}
                >
                  {t(`notify.${ch}`)}
                </button>
              ))}
            </div>
          </div>
          <NeuButton type="button" variant="primary" disabled={c.busy || c.mix} className="flex items-center gap-2 justify-self-start px-6 py-3" data-testid="notify-send" onClick={c.send}>
            <Send className="h-4 w-4" /> {t("notify.send")}
          </NeuButton>
        </NeuCard>

        <SentHistory />
      </div>
    </AppShell>
  );
}
